import { useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../lib/firebase";
import { useLanguage } from "../contexts/LanguageContext";
import Logo from "../components/Logo";
import LanguageSelector from "../components/LanguageSelector";
import { getFirebaseErrorMessage } from "../utils/firebaseErrorHandler";

export default function Login() {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const authPendingRef = useRef(false);

  // ProtectedRoute parks the page the user actually asked for in `from`, query
  // string included. Returning them there is what makes an emailed deep link
  // such as /setup?support=chat survive a login. Only same-origin paths are
  // honoured ("//evil.com" is a protocol-relative URL, not a route), so a
  // crafted history entry cannot bounce anyone off the app.
  const from = location.state?.from;
  const redirectTo =
    typeof from === "string" && from.startsWith("/") && !from.startsWith("//")
      ? from
      : "/dashboard";

  const handleSubmit = async (e) => {
    // preventDefault before the guard, or a blocked submit falls through to a
    // native form post.
    e.preventDefault();
    if (authPendingRef.current) return;
    authPendingRef.current = true;
    setError("");
    try {
      setIsLoading(true);
      const result = await signInWithEmailAndPassword(auth, email, password);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await result.user.getIdToken(true);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      authPendingRef.current = false;
      setIsLoading(false);
      setError(getFirebaseErrorMessage(error, t));
    }
  };

  const handleGoogleSignIn = async () => {
    // `isLoading` is a render-time value, so two clicks dispatched before React
    // re-renders both read false — as does the disabled attribute, which lands a
    // render later. A ref updates synchronously and closes that window. Firebase
    // corrupts its own promise bookkeeping if a second popup starts while one is
    // pending ("INTERNAL ASSERTION FAILED: Pending promise was never set").
    if (authPendingRef.current) return;
    authPendingRef.current = true;
    setError("");
    try {
      setIsLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await result.user.getIdToken(true);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      authPendingRef.current = false;
      setIsLoading(false);
      if (error?.code === "auth/popup-closed-by-user") return;
      setError(getFirebaseErrorMessage(error, t));
    }
  };

  return (
    <>
      <div className="h-screen flex flex-col justify-center bg-gray-50">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <div className="flex justify-center">
          <Logo />
        </div>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            {t("auth.login.title")}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t("auth.login.noAccount")}{" "}
            <Link
              to="/signup"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {t("auth.login.signUpLink")}
            </Link>
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white p-10 shadow-lg sm:rounded-lg">
            {error && (
              <div className="mb-4 rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}
            <div className="mb-6">
              <div className="mb-6">
                <button
                  id="google-signin-button"
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                  className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  {t("auth.login.signInWithGoogle")}
                </button>
              </div>

              <div className="relative mt-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-gray-500">
                    {t("auth.login.continueWith")}
                  </span>
                </div>
              </div>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("auth.login.emailLabel")}
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-xs focus:border-indigo-500 focus:outline-hidden focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("auth.login.passwordLabel")}
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-xs focus:border-indigo-500 focus:outline-hidden focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    {t("auth.login.rememberMe")}
                  </label>
                </div>

                <div className="text-sm">
                  <Link
                    to="/reset-password"
                    className="font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    {t("auth.login.forgotPassword")}
                  </Link>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  id="signin-button"
                  disabled={isLoading}
                  className={`
                    flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-xs hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                      ${isLoading ? "opacity-50 cursor-not-allowed" : ""}
                  `}
                >
                  {isLoading
                    ? t("common.loading")
                    : t("auth.login.signInButton")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
